import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Radio, RadioGroup,
  FormControlLabel, FormControl, Checkbox, IconButton,
  InputAdornment, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip, Fade, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Select, MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon, Save as SaveIcon, Clear as ClearIcon,
  CloudDownload, Refresh as RefreshIcon, Add as AddIcon, Close as CloseIcon,
  HealthAndSafety as InsuranceIcon, MedicalServices as ServiceIcon,
  CheckCircle as CheckCircleIcon, Delete as DeleteIcon,   SwapHoriz as MoveIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';


import {
  loadInsurance, loadServices, getServiceInsurances,
  updateInsuredService, deleteInsuredService,
  saveNewService, moveServicesToInsurancedb,
} from '../BaseURL';
import {
  AdminPageHead, ADMIN, EmptyState, BulkEditBar,
  adminFieldSx, brandButtonSx, brandGhostButtonSx,
} from './adminUi';

// The bulk-edit bar's fields. Blank means "leave this column alone", so the
// starting state is all-blank and pressing Apply with nothing typed is a no-op.
//
// `service` is the service NAME. It is here because correcting a spelling
// otherwise means editing it once per scheme — and /updateInsuredService now
// accepts it optionally for exactly this.
const EMPTY_BULK = { service: '', amo: '', toup: '', code: '' };

const BULK_FIELDS = [
  { key: 'service', label: 'Service name', placeholder: 'Leave blank to keep' },
  { key: 'amo',     label: 'Tariff',       type: 'number', adornment: 'GH¢' },
  { key: 'toup',    label: 'Top-up',       type: 'number', adornment: 'GH¢' },
  { key: 'code',    label: 'GDRG Code',    placeholder: 'e.g. MEDI07A' },
];

// ============================================================================
//  Services Setup
// ============================================================================
//
//  ON COLOUR
//
//    This screen carried FOUR unrelated palettes: a teal→lime gradient
//    (#11998e → #38ef7d) on the page banner, the dialog header and every
//    primary button; an indigo set (#667eea / #3949ab / #1a237e) on the service
//    list; a peach panel tint (#fff8f3); and MUI's default greys. 125 colour
//    literals in one file, and the lime gradient appeared three times at full
//    width, which is why the screen shouted.
//
//    All of it now resolves to the navy + apex green in brand.js.
//
//  ON THE EMPTY SPACE AT THE BOTTOM
//
//    Both panels were `height: 580` — a fixed height regardless of content. Two
//    records therefore left roughly 400px of blank white under them, and the
//    dialog's provider box reserved `minHeight: 280` to show two checkboxes.
//    Nothing sizes itself to a constant any more; the long list caps its own
//    scroll instead.

// One choice out of a small set, drawn as a bordered tile rather than a loose
// radio. Three bare circles in a row gave no sense of being one decision, and
// 'Main Services' wrapped awkwardly against its dot.
//
// The selected tile is FILLED, not merely outlined. Outline-only was the first
// attempt and it did not work: a green border is what MUI also uses for focus,
// so the tile you had just tabbed to looked exactly like the tile you had
// chosen, and on a fresh form nothing looked chosen at all.
const svcChoiceSx = (on) => ({
  m: 0,
  px: 1.25, py: .625,
  borderRadius: '8px',
  border: `1px solid ${on ? '#2E9E4F' : '#DDE5EF'}`,
  background: on ? '#2E9E4F' : '#fff',
  color: on ? '#fff' : '#5A6B87',
  transition: 'border-color .15s ease, background-color .15s ease, color .15s ease',
  '&:hover': { borderColor: on ? '#217A3C' : '#1D4E8F', background: on ? '#217A3C' : '#F4F7FB' },
});

// Section heading inside the New Service dialog: a small green rule, the name,
// and a hairline running to the edge — so the sections read as separate groups
// without four heavy boxes stacked down the page.
const SvcSectionLabel = ({ children, error }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
    <Box sx={{ width: 3, height: 14, borderRadius: 1, background: error ? '#D64545' : '#2E9E4F', flexShrink: 0 }} />
    <Typography sx={{
      fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
      color: error ? '#D64545' : '#5A6B87', whiteSpace: 'nowrap',
    }}>
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', background: '#DDE5EF' }} />
  </Box>
);

/* ─────────────────────────────────────────────────────────────
   Shared compact styles
───────────────────────────────────────────────────────────── */
const cellSelectSx = {
  fontSize: '0.78rem',
  '& .MuiSelect-select': { py: '4px', px: '8px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DDE5EF' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#123A6D' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#123A6D' },
};
const cellInputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.78rem',
    '& input': { py: '4px', px: '8px' },
    '& fieldset': { borderColor: '#DDE5EF' },
    '&:hover fieldset': { borderColor: '#123A6D' },
    '&.Mui-focused fieldset': { borderColor: '#123A6D' },
  },
};

/* ─────────────────────────────────────────────────────────────
   Editable table row
───────────────────────────────────────────────────────────── */
const EditableRow = ({ row, idx, selected, onSelectToggle, onChange, onUpdate, onDelete, updating, deleting }) => {
  const tdSx = { borderBottom: '1px solid #DDE5EF', py: 0.75, px: 1 };
  return (
    <TableRow sx={{
      background: selected ? '#E8EEF7' : '#fff',
      '&:hover': { background: selected ? '#E8EEF7' : '#F4F7FB' },
      transition: 'background-color .15s ease',
    }}>
      {/* Tick box, so a row can be included in the "apply to many" bar above.
          Services Setup had no selection at all — every scheme's tariff had to
          be typed and saved one row at a time. */}
      <TableCell sx={{ ...tdSx, width: 44, textAlign: 'center', px: 0.5 }}>
        <Checkbox
          size="small"
          checked={!!selected}
          onChange={() => onSelectToggle(row.id)}
          sx={{ p: 0.5, color: '#93A2B8', '&.Mui-checked': { color: '#2E9E4F' } }}
        />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 36, textAlign: 'center', color: '#93A2B8', fontSize: '0.72rem', fontWeight: 500 }}>{idx + 1}</TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 130 }}>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0F1F3D' }}>{row.STATUS || '—'}</Typography>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 140 }}>
        <Select size="small" fullWidth value={row.charge_status || ''} onChange={e => onChange('charge_status', e.target.value)} displayEmpty sx={cellSelectSx}>
          <MenuItem value="" disabled><em style={{ color: '#bbb', fontSize: '0.75rem' }}>Select…</em></MenuItem>
          <MenuItem value="Auto">Auto Services</MenuItem>
          <MenuItem value="Main Services">Main Services</MenuItem>
          <MenuItem value="Manual">Manual Services</MenuItem>
        </Select>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <TextField size="small" type="number" fullWidth value={row.amo ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('amo', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 95 }}>
        <TextField size="small" type="number" fullWidth value={row.toup ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('toup', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <Select size="small" fullWidth value={row.age || ''} onChange={e => onChange('age', e.target.value)} displayEmpty sx={cellSelectSx}>
          <MenuItem value="" disabled><em style={{ color: '#bbb', fontSize: '0.75rem' }}>Select…</em></MenuItem>
          <MenuItem value="Adult">Adult</MenuItem>
          <MenuItem value="Child">Child</MenuItem>
          <MenuItem value="Both">Both</MenuItem>
        </Select>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" fullWidth value={row.code ?? ''} onChange={e => onChange('code', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 180, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
          <Button size="small" variant="contained" onClick={() => onUpdate(row)} disabled={updating || deleting}
            startIcon={updating ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            sx={{ fontSize: '0.7rem', px: 1.5, py: 0.5, background: 'linear-gradient(135deg,#3FBF64,#217A3C)', color: 'white', fontWeight: 600, textTransform: 'none', boxShadow: '0 2px 8px rgba(46,158,79,0.25)', '&:hover': { background: 'linear-gradient(135deg,#2E9E4F,#217A3C)' }, '&:disabled': { background: '#DDE5EF', color: '#93A2B8' } }}>
            {updating ? 'Saving...' : 'Update'}
          </Button>
          <Button size="small" variant="contained" onClick={() => onDelete(row)} disabled={deleting || updating}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            sx={{ fontSize: '0.7rem', px: 1.5, py: 0.5, background: '#D64545', color: 'white', fontWeight: 600, textTransform: 'none', boxShadow: '0 2px 8px rgba(214,69,69,0.25)', '&:hover': { background: '#B23A3A' }, '&:disabled': { background: '#DDE5EF', color: '#93A2B8' } }}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
};

/* ─────────────────────────────────────────────────────────────
   New-Service Dialog
───────────────────────────────────────────────────────────── */
const NewServiceDialog = ({ open, onClose, insuranceProviders, loadingInsurance, onSaved }) => {
  const [saving, setSaving] = useState(false);

  // ── form state ────────────────────────────────────────────
  const [chargeStatus, setChargeStatus]                     = useState('');
  const [ageStatus, setAgeStatus]                           = useState('');
  const [serviceName, setServiceName]                       = useState('');
  const [insuranceTariff, setInsuranceTariff]               = useState('');
  const [topUp, setTopUp]                                   = useState('');
  const [specialityDescription, setSpecialityDescription]   = useState('');
  const [specialityCode, setSpecialityCode]                 = useState('');
  const [selectedInsurances, setSelectedInsurances]         = useState({});
  const [insuranceSearch, setInsuranceSearch]               = useState('');
  const [errors, setErrors]                                 = useState({});

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

  const clearError = (field) => setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

  const handleInsuranceToggle = (id) => {
    setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
    clearError('insurance');
  };
  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { all[i.id] = true; });
    setSelectedInsurances(all);
    clearError('insurance');
  };
  const handleDeselectAll = () => setSelectedInsurances({});

  /* ── validation ──────────────────────────────────────────── */
  const validate = () => {
    const e = {};

    if (!chargeStatus)
      e.chargeStatus = 'Charge Status is required';

    if (!ageStatus)
      e.ageStatus = 'Age Status is required';

    if (!serviceName.trim())
      e.serviceName = 'Service Name is required';

    // Tariff: required, must be a number ≥ 0 (0 is allowed)
    if (insuranceTariff === '' || insuranceTariff === null || insuranceTariff === undefined)
      e.insuranceTariff = 'Insurance Tariff is required';
    else if (isNaN(parseFloat(insuranceTariff)) || parseFloat(insuranceTariff) < 0)
      e.insuranceTariff = 'Must be a valid number (0 or more)';

    // Top-up: same rule
    if (topUp === '' || topUp === null || topUp === undefined)
      e.topUp = 'Top-up is required';
    else if (isNaN(parseFloat(topUp)) || parseFloat(topUp) < 0)
      e.topUp = 'Must be a valid number (0 or more)';

    if (!specialityCode.trim())
      e.specialityCode = 'Speciality Code is required';

    if (selectedCount === 0)
      e.insurance = 'At least one insurance provider must be selected';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── save handler ────────────────────────────────────────── */
  const handleSave = async () => {
    if (!validate()) {
      // Collect human-readable messages for the alert
      const pending = {
        ...(!chargeStatus                                                             ? { a: 'Charge Status must be selected'              } : {}),
        ...(!ageStatus                                                                ? { b: 'Age Status must be selected'                 } : {}),
        ...(!serviceName.trim()                                                       ? { c: 'Service Name must be entered'                } : {}),
        ...(insuranceTariff === ''                                                    ? { d: 'Insurance Tariff must be entered'            } : {}),
        ...(topUp === ''                                                              ? { e: 'Top-up must be entered'                     } : {}),
        ...(!specialityCode.trim()                                                    ? { f: 'Speciality Code must be entered'             } : {}),
        ...(selectedCount === 0                                                       ? { g: 'At least one insurance must be selected'    } : {}),
      };
      await Swal.fire({
        title: 'Please complete all required fields',
        html: `<ul style="text-align:left;margin:0;padding-left:1.2em;">${Object.values(pending).map(m => `<li>${m}</li>`).join('')}</ul>`,
        icon: 'warning',
        confirmButtonColor: '#2E9E4F',
      });
      return;
    }

    setSaving(true);
    try {
      /*
       * One row per selected insurance → one INSERT per row in insured_services
       *   charge_status  ← chargeStatus
       *   age            ← ageStatus
       *   service        ← serviceName
       *   amo            ← insuranceTariff
       *   toup           ← topUp
       *   code           ← specialityCode
       *   STATUS         ← insurance provider name (the label column)
       
       */
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        return {
          charge_status:           chargeStatus,
          age:                     ageStatus,
          service:                 serviceName.trim(),
          amo:                     parseFloat(insuranceTariff),
          toup:                    parseFloat(topUp),
          code:                    specialityCode.trim(),
          speciality_description:  specialityDescription.trim(),
          STATUS:                  provider?.name || ''
        };
      });

      const response = await axios.post(saveNewService, { rows });

      if (response.data.success) {
        await Swal.fire({
          title: 'Saved!',
          html: `<b>${serviceName}</b> saved with <b>${selectedCount}</b> insurance record${selectedCount !== 1 ? 's' : ''}.`,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        if (onSaved) onSaved();
        handleClose();
      } else {
        throw new Error(response.data.message || 'Save failed');
      }
    } catch (err) {
      await Swal.fire({
        title: 'Save Failed',
        text: err.response?.data?.message || err.message || 'An error occurred while saving',
        icon: 'error',
        confirmButtonColor: '#D64545',
      });
    } finally {
      setSaving(false);
    }
  };

  /* ── reset & close ───────────────────────────────────────── */
  const handleClose = () => {
    setChargeStatus(''); setAgeStatus(''); setServiceName('');
    setInsuranceTariff(''); setTopUp('');
    setSpecialityDescription(''); setSpecialityCode('');
    setSelectedInsurances({}); setInsuranceSearch('');
    setErrors({});
    onClose();
  };

  const filteredProviders = insuranceProviders.filter(i =>
    i.name?.toLowerCase().includes(insuranceSearch.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' } }}>

      <DialogTitle sx={{ background: 'linear-gradient(135deg,#123A6D,#0B2452)', color: 'white', px: { xs: 2, sm: 2.5 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
            background: 'linear-gradient(135deg,#3FBF64,#217A3C)',
            display: 'grid', placeItems: 'center',
          }}>
            <ServiceIcon sx={{ fontSize: 19 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>New Service</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,.75)', mt: .2 }}>
              What it costs, and which schemes it applies to
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small"
          sx={{ color: 'white', flexShrink: 0, bgcolor: 'rgba(255,255,255,.12)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* STACKED sections, full width — not two columns.
            Two columns was the imbalance: the left held six rows of fields and
            the right held a search box and two checkboxes, so the right half of
            the dialog was mostly empty and the divider drew attention to it.
            Splitting by TOPIC down the page instead means each section is as
            tall as it needs to be and nothing is left half-filled — and the
            scheme list can grow to fifteen schemes without the left column
            having to grow with it. */}
        <Box sx={{ p: { xs: 2, sm: 2.5 }, display: 'grid', gap: 2.5 }}>

          {/* ── The service itself ─────────────────────────── */}
          <Box>
            <SvcSectionLabel>The service</SvcSectionLabel>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' } }}>
              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <TextField fullWidth size="small" label="Service Name *" value={serviceName}
                  onChange={e => { setServiceName(e.target.value); clearError('serviceName'); }}
                  error={!!errors.serviceName} helperText={errors.serviceName || ' '} sx={adminFieldSx} />
              </Box>

              <TextField fullWidth size="small" label="Speciality Code *" value={specialityCode}
                onChange={e => { setSpecialityCode(e.target.value); clearError('specialityCode'); }}
                error={!!errors.specialityCode} helperText={errors.specialityCode || ' '} sx={adminFieldSx} />

              <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                <TextField fullWidth size="small" label="Speciality Description" value={specialityDescription}
                  onChange={e => setSpecialityDescription(e.target.value)}
                  helperText="Optional" sx={adminFieldSx} />
              </Box>
            </Box>
          </Box>

          {/* ── Pricing ───────────────────────────────────── */}
          <Box>
            <SvcSectionLabel>Pricing</SvcSectionLabel>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' } }}>
              <TextField fullWidth size="small" label="Insurance Tariff *" type="number"
                value={insuranceTariff}
                onChange={e => { setInsuranceTariff(e.target.value); clearError('insuranceTariff'); }}
                error={!!errors.insuranceTariff} helperText={errors.insuranceTariff || 'What the scheme pays'}
                InputProps={{ startAdornment: <InputAdornment position="start"><Box component="span" sx={{ fontSize: 12, color: '#93A2B8', fontWeight: 600 }}>GH¢</Box></InputAdornment> }}
                inputProps={{ min: 0, step: '0.01' }} sx={adminFieldSx} />

              <TextField fullWidth size="small" label="Top-up *" type="number"
                value={topUp}
                onChange={e => { setTopUp(e.target.value); clearError('topUp'); }}
                error={!!errors.topUp} helperText={errors.topUp || 'What the patient pays'}
                InputProps={{ startAdornment: <InputAdornment position="start"><Box component="span" sx={{ fontSize: 12, color: '#93A2B8', fontWeight: 600 }}>GH¢</Box></InputAdornment> }}
                inputProps={{ min: 0, step: '0.01' }} sx={adminFieldSx} />
            </Box>
          </Box>

          {/* ── Rules ─────────────────────────────────────── */}
          <Box>
            <SvcSectionLabel error={!!(errors.chargeStatus || errors.ageStatus)}>How it is charged</SvcSectionLabel>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
              {/* Segmented choices. Each tile is fully clickable and the
                  selected one is filled, not merely outlined — the outline
                  alone was indistinguishable from focus. */}
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 11, letterSpacing: '.5px', textTransform: 'uppercase', color: errors.chargeStatus ? '#D64545' : '#93A2B8', mb: .75, display: 'block' }}>
                  Charge status *
                </Typography>
                <RadioGroup row value={chargeStatus} sx={{ gap: .75 }}
                  onChange={e => { setChargeStatus(e.target.value); clearError('chargeStatus'); }}>
                  {['Auto', 'Main Services', 'Manual'].map(v => (
                    <FormControlLabel key={v} value={v} sx={svcChoiceSx(chargeStatus === v)}
                      control={<Radio size="small" sx={{ p: .25, mr: .5, color: '#93A2B8', '&.Mui-checked': { color: '#fff' } }} />}
                      label={<Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{v}</Typography>} />
                  ))}
                </RadioGroup>
                {errors.chargeStatus && <Typography sx={{ color: '#D64545', fontSize: 11, mt: .5 }}>{errors.chargeStatus}</Typography>}
              </FormControl>

              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 11, letterSpacing: '.5px', textTransform: 'uppercase', color: errors.ageStatus ? '#D64545' : '#93A2B8', mb: .75, display: 'block' }}>
                  Applies to *
                </Typography>
                <RadioGroup row value={ageStatus} sx={{ gap: .75 }}
                  onChange={e => { setAgeStatus(e.target.value); clearError('ageStatus'); }}>
                  {['Adult', 'Child', 'Both'].map(v => (
                    <FormControlLabel key={v} value={v} sx={svcChoiceSx(ageStatus === v)}
                      control={<Radio size="small" sx={{ p: .25, mr: .5, color: '#93A2B8', '&.Mui-checked': { color: '#fff' } }} />}
                      label={<Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{v}</Typography>} />
                  ))}
                </RadioGroup>
                {errors.ageStatus && <Typography sx={{ color: '#D64545', fontSize: 11, mt: .5 }}>{errors.ageStatus}</Typography>}
              </FormControl>
            </Box>
          </Box>

          {/* ── RIGHT: insurance checkboxes ────────────────── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {/* The count was stated twice — "0 of 2 selected" under the heading
                AND a "N selected" chip beside it. Once, in the heading. */}
            <SvcSectionLabel error={!!errors.insurance}>
              Insurance schemes *
              <Box component="span" sx={{
                ml: 1, letterSpacing: 0, textTransform: 'none',
                color: selectedCount > 0 ? '#217A3C' : '#93A2B8',
              }}>
                {selectedCount} of {insuranceProviders.length} selected
              </Box>
            </SvcSectionLabel>

            {errors.insurance && (
              <Typography sx={{ color: '#D64545', fontSize: 11, mb: 1 }}>
                {errors.insurance}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 1.25, alignItems: 'center' }}>
              <TextField fullWidth size="small" placeholder="Search schemes…"
                value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)}
                sx={adminFieldSx}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: '#93A2B8' }} /></InputAdornment>) }} />
              {/* Two outlined buttons the same size as the primary action was
                  disproportionate for what they do; they are text links now. */}
              <Button size="small" onClick={handleSelectAll}
                sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, color: '#123A6D', whiteSpace: 'nowrap', minWidth: 0, px: 1 }}>
                All
              </Button>
              <Button size="small" onClick={handleDeselectAll}
                sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, color: '#5A6B87', whiteSpace: 'nowrap', minWidth: 0, px: 1 }}>
                None
              </Button>
            </Box>

            {/* No reserved minHeight at all, and the schemes lay out as a
                multi-column grid of tiles. The old box reserved 280px of white
                and stacked two checkboxes down its left edge, which is where
                most of the emptiness in this dialog came from. As a grid, two
                schemes take one short row and twenty take five — the box is
                always exactly as tall as its contents. */}
            <Box sx={{
              overflowY: 'auto',
              border: `1px solid ${errors.insurance ? '#D64545' : '#DDE5EF'}`,
              borderRadius: '10px', p: .875, maxHeight: 260,
              transition: 'border-color 0.2s',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' },
            }}>
              {loadingInsurance ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : filteredProviders.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                  <Typography sx={{ fontSize: 12.5, color: '#5A6B87' }}>No schemes found</Typography>
                </Box>
              ) : (
                <Box sx={{
                  display: 'grid', gap: .75,
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
                }}>
                {filteredProviders.map(insurance => {
                  const on = !!selectedInsurances[insurance.id];
                  return (
                  <Box key={insurance.id} onClick={() => handleInsuranceToggle(insurance.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', px: 1, py: .625, borderRadius: '8px',
                      cursor: 'pointer', minWidth: 0,
                      background: on ? '#E7F5EC' : '#fff',
                      border: `1px solid ${on ? '#2E9E4F' : '#DDE5EF'}`,
                      transition: 'background-color .15s ease, border-color .15s ease',
                      '&:hover': { borderColor: on ? '#217A3C' : '#1D4E8F' },
                    }}>
                    <Checkbox size="small"
                      checked={on}
                      onChange={() => handleInsuranceToggle(insurance.id)}
                      onClick={e => e.stopPropagation()}
                      sx={{ p: 0.25, flexShrink: 0, color: '#93A2B8', '&.Mui-checked': { color: '#2E9E4F' } }} />
                    <Typography noWrap title={insurance.name}
                      sx={{ ml: .75, fontSize: 12, fontWeight: on ? 700 : 500, color: on ? '#217A3C' : '#0F1F3D' }}>
                      {insurance.name}
                    </Typography>
                  </Box>
                  );
                })}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: { xs: 2, sm: 2.5 }, py: 1.75,
        borderTop: '1px solid #DDE5EF', background: '#fff', gap: 1.25,
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        '& > *': { width: { xs: '100%', sm: 'auto' }, ml: { xs: '0 !important', sm: undefined } },
      }}>
        <Button onClick={handleClose} startIcon={<ClearIcon />} variant="outlined" disabled={saving}
          sx={{ ...brandGhostButtonSx, py: 1, px: 2.5, fontSize: 13.5 }}>
          Cancel
        </Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
          onClick={handleSave} disabled={saving}
          sx={{ ...brandButtonSx, py: 1, px: 2.5, fontSize: 13.5 }}>
          {saving ? 'Saving…' : 'Save service'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────── */
const ServicesSetting = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingServiceInsurances, setLoadingServiceInsurances] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState(null);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceInsuranceRows, setServiceInsuranceRows] = useState([]);
  // Which scheme ROWS are ticked for the bulk bar. Distinct from
  // `selectedServiceNames` below, which is the left-hand list's move selection.
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkValues, setBulkValues] = useState(EMPTY_BULK);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [insuranceFilter, setInsuranceFilter] = useState('');

  // ── Move services state ───────────────────────────────────
const [selectedServiceNames, setSelectedServiceNames] = useState(new Set());
const [moveDialogOpen, setMoveDialogOpen]             = useState(false);
const [moveTargetInsurance, setMoveTargetInsurance]   = useState('');
const [moving, setMoving]                             = useState(false);
const [selectMode, setSelectMode]                     = useState(false);

  const loadInsuranceProviders = async () => {
    setLoadingInsurance(true); setError(null);
    try {
      const res = await axios.get(loadInsurance);
      if (res.data.success) setInsuranceProviders(res.data.results);
      else setError('Failed to load insurance providers');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load insurance providers');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load insurance providers', icon: 'error' });
    } finally { setLoadingInsurance(false); }
  };

  const loadServicesFromBackend = async () => {
    setLoadingServices(true); setError(null);
    try {
      const res = await axios.get(loadServices);
      if (res.data.success) setServices(res.data.results);
      else setError('Failed to load services');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load services');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load services', icon: 'error' });
    } finally { setLoadingServices(false); }
  };

  const loadServiceInsurancesForDisplay = async (serviceId, serviceName) => {
    setLoadingServiceInsurances(true); setServiceInsuranceRows([]);
    try {
      const res = await axios.post(getServiceInsurances, { serviceId, serviceName });
      if (res.data.success) setServiceInsuranceRows(res.data.results);
    } catch (err) {
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load service insurances', icon: 'error' });
    } finally { setLoadingServiceInsurances(false); }
  };

  const handleUpdateRow = async (row) => {
    if (!row.charge_status || !row.amo || !row.toup || !row.age) {
      await Swal.fire({ title: 'Validation Error', text: 'Please fill in all required fields (Charge Status, Tariff, Top-up, Age)', icon: 'warning' }); return;
    }
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot update.', icon: 'error' }); return; }
    setUpdatingRowId(row.id);
    try {
      const res = await axios.post(updateInsuredService, { id: row.id, charge_status: row.charge_status, amo: parseFloat(row.amo), toup: parseFloat(row.toup), age: row.age, code: row.code || '' });
      if (res.data.success) {
        await Swal.fire({ title: 'Success!', text: `Successfully updated ${row.STATUS}`, icon: 'success', timer: 2000, showConfirmButton: false });
        if (selectedService) await loadServiceInsurancesForDisplay(selectedService.id, selectedService.service);
      } else throw new Error(res.data.message || 'Update failed');
    } catch (err) {
      await Swal.fire({ title: 'Update Failed', text: err.response?.data?.message || err.message || 'Failed to update', icon: 'error' });
    } finally { setUpdatingRowId(null); }
  };

  const handleDeleteRow = async (row) => {
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot delete.', icon: 'error' }); return; }
    const result = await Swal.fire({
      title: 'Confirm Delete',
      html: `Are you sure you want to delete the record for <strong>${row.STATUS || 'this entry'}</strong>?<br/><span style="color:#D64545;font-size:0.9rem;">This action cannot be undone.</span>`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#D64545', cancelButtonColor: '#93A2B8',
      confirmButtonText: 'Yes, Delete', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setDeletingRowId(row.id);
    try {
      const res = await axios.post(deleteInsuredService, { id: row.id });
      if (res.data.success) {
        await Swal.fire({ title: 'Deleted!', text: `Record for "${row.STATUS}" has been deleted.`, icon: 'success', timer: 2000, showConfirmButton: false });
        setServiceInsuranceRows(prev => prev.filter(r => r.id !== row.id));
      } else throw new Error(res.data.message || 'Delete failed');
    } catch (err) {
      await Swal.fire({ title: 'Delete Failed', text: err.response?.data?.message || err.message || 'Failed to delete', icon: 'error' });
    } finally { setDeletingRowId(null); }
  };

  useEffect(() => { loadInsuranceProviders(); loadServicesFromBackend(); }, []);

  const handleServiceClick = async (service) => {
    if (selectedService?.id === service.id) { setSelectedService(null); setServiceInsuranceRows([]); setSelectedRowIds([]); return; }
    setSelectedService(service);
    setSelectedRowIds([]);
    await loadServiceInsurancesForDisplay(service.id, service.service);
  };

  const handleRowChange = (idx, field, value) =>
    setServiceInsuranceRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  /* ─────────────────────────────────────────────────────────────
     Bulk edit bar — apply typed values / delete, across ticked rows
     ───────────────────────────────────────────────────────────── */
  //
  //  Loops the existing single-row endpoints rather than a new bulk SQL path:
  //  /updateInsuredService and /deleteInsuredService are already validated and
  //  in daily use, and a service has only a handful of schemes.

  const handleRowSelectionToggle = (rowId) => {
    setSelectedRowIds(prev =>
      prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
    );
  };

  const handleSelectAllRows = () => {
    setSelectedRowIds(prev =>
      prev.length === serviceInsuranceRows.length ? [] : serviceInsuranceRows.map(r => r.id)
    );
  };

  const bulkTargets = () => serviceInsuranceRows.filter(r => selectedRowIds.includes(r.id));

  const handleBarApply = async () => {
    const targets = bulkTargets();
    if (targets.length === 0) return;

    // Blank means "leave alone" — only what was typed is applied.
    const patch = {};
    if (String(bulkValues.service).trim() !== '') patch.service = bulkValues.service.trim();
    if (String(bulkValues.amo).trim()     !== '') patch.amo     = bulkValues.amo.trim();
    if (String(bulkValues.toup).trim()    !== '') patch.toup    = bulkValues.toup.trim();
    if (String(bulkValues.code).trim()    !== '') patch.code    = bulkValues.code.trim();
    if (Object.keys(patch).length === 0) return;

    const renaming = !!patch.service;

    const confirm = await Swal.fire({
      title: `Apply to ${targets.length} scheme${targets.length === 1 ? '' : 's'}?`,
      html: `<div style="text-align:left;font-size:13px">
               ${Object.entries(patch).map(([k, v]) => `<div><b>${k === 'amo' ? 'tariff' : k === 'toup' ? 'top-up' : k}</b> → ${v}</div>`).join('')}
               <div style="margin-top:8px;color:#5A6B87">Fields left blank keep their current values.</div>
               ${renaming ? '<div style="margin-top:6px;color:#8A5A12">Renaming the service changes it on the ticked rows only — untick nothing if you mean to rename it everywhere.</div>' : ''}
             </div>`,
      icon: 'question', showCancelButton: true,
      confirmButtonColor: '#123A6D', cancelButtonColor: '#5A6B87',
      confirmButtonText: 'Yes, apply',
    });
    if (!confirm.isConfirmed) return;

    setBulkApplying(true);
    let ok = 0; const failed = [];
    for (const row of targets) {
      try {
        // The endpoint writes every column it receives, so the row's current
        // values stand in wherever the patch is silent. `service` is the one
        // exception: it is only sent when set, because the endpoint leaves the
        // column alone when it is absent.
        const body = {
          id:            row.id,
          charge_status: row.charge_status,
          amo:           patch.amo  ?? row.amo,
          toup:          patch.toup ?? row.toup,
          age:           row.age,
          code:          patch.code ?? (row.code || ''),
        };
        if (patch.service) body.service = patch.service;

        const res = await axios.post(updateInsuredService, body);
        if (res.data.success) ok++; else failed.push(row.STATUS || row.id);
      } catch (_) { failed.push(row.STATUS || row.id); }
    }
    setBulkApplying(false);

    // A rename changes the very key this panel looks rows up by
    // (getServiceInsurances matches on `service`), so the left-hand list has to
    // be reloaded too or the selected entry points at a name that is gone.
    if (renaming) {
      await loadServicesFromBackend();
      setSelectedService(prev => (prev ? { ...prev, service: patch.service } : prev));
      await loadServiceInsurancesForDisplay(selectedService?.id, patch.service);
    } else if (selectedService) {
      await loadServiceInsurancesForDisplay(selectedService.id, selectedService.service);
    }
    setBulkValues(EMPTY_BULK);
    setSelectedRowIds([]);

    await Swal.fire({
      title: failed.length === 0 ? 'Applied' : 'Partly applied',
      text: failed.length === 0
        ? `${ok} scheme${ok === 1 ? '' : 's'} updated.`
        : `${ok} updated. Failed: ${failed.join(', ')}.`,
      icon: failed.length === 0 ? 'success' : 'warning',
      timer: failed.length === 0 ? 2200 : undefined,
      showConfirmButton: failed.length > 0,
    });
  };

  const handleBarDelete = async () => {
    const targets = bulkTargets();
    if (targets.length === 0) return;

    const confirm = await Swal.fire({
      title: `Delete ${targets.length} record${targets.length === 1 ? '' : 's'}?`,
      html: `<div style="text-align:left;font-size:13px">
               ${targets.map(r => `<div>• ${r.STATUS || r.id}</div>`).join('')}
               <div style="margin-top:8px;color:#B23A3A">This cannot be undone.</div>
             </div>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#D64545', cancelButtonColor: '#5A6B87',
      confirmButtonText: `Yes, delete ${targets.length}`,
    });
    if (!confirm.isConfirmed) return;

    setBulkDeleting(true);
    let ok = 0; const failed = [];
    for (const row of targets) {
      try {
        const res = await axios.post(deleteInsuredService, { id: row.id });
        if (res.data.success) ok++; else failed.push(row.STATUS || row.id);
      } catch (_) { failed.push(row.STATUS || row.id); }
    }
    setBulkDeleting(false);
    setSelectedRowIds([]);

    if (selectedService) {
      await loadServiceInsurancesForDisplay(selectedService.id, selectedService.service);
    }

    await Swal.fire({
      title: failed.length === 0 ? 'Deleted' : 'Partly deleted',
      text: failed.length === 0
        ? `${ok} record${ok === 1 ? '' : 's'} removed.`
        : `${ok} removed. Failed: ${failed.join(', ')}.`,
      icon: failed.length === 0 ? 'success' : 'warning',
      timer: failed.length === 0 ? 2200 : undefined,
      showConfirmButton: failed.length > 0,
    });
  };



  const handleMoveServices = async () => {
  if (selectedServiceNames.size === 0 || !moveTargetInsurance) return;
  setMoving(true);
  try {
    const res = await axios.post(moveServicesToInsurancedb, {
      serviceNames:    Array.from(selectedServiceNames),
      targetInsurance: moveTargetInsurance,
    });
    if (res.data.success) {
      await Swal.fire({
        title: 'Done!',
        text: res.data.message,
        icon: 'success',
        timer: 3000,
        showConfirmButton: false,
        timerProgressBar: true,
      });
      setMoveDialogOpen(false);
      setSelectedServiceNames(new Set());
      setMoveTargetInsurance('');
      setSelectMode(false);
      loadServicesFromBackend();
    } else {
      throw new Error(res.data.message);
    }
  } catch (err) {
    await Swal.fire({
      title: 'Move Failed',
      text: err.response?.data?.message || err.message,
      icon: 'error',
    });
  } finally {
    setMoving(false);
  }
};

const toggleServiceSelection = (serviceName) => {
  setSelectedServiceNames(prev => {
    const next = new Set(prev);
    next.has(serviceName) ? next.delete(serviceName) : next.add(serviceName);
    return next;
  });
};

const handleSelectAllVisible = (visibleServices) => {
  setSelectedServiceNames(new Set(visibleServices.map(s => s.service)));
};

const handleDeselectAll = () => setSelectedServiceNames(new Set());

  return (
    <>
      {/* One page header, brand navy, replacing the full-width teal→lime band.
          The two reload buttons moved in here beside New Service; they were
          three competing "contained" buttons on a gradient, two of them
          translucent white on green, which is the combination that made the
          bar read as loud and unfinished. */}
      <AdminPageHead
        icon={ServiceIcon}
        title="Services Setup"
        subtitle="Service tariffs and top-ups, per insurance scheme"
        actions={
          <>
            <Tooltip title="Reload services" arrow>
              <span>
                <IconButton
                  onClick={loadServicesFromBackend} disabled={loadingServices} size="small"
                  aria-label="Reload services"
                  sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.14)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}
                >
                  {loadingServices ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reload insurance schemes" arrow>
              <span>
                <IconButton
                  onClick={loadInsuranceProviders} disabled={loadingInsurance} size="small"
                  aria-label="Reload insurance schemes"
                  sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.14)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}
                >
                  {loadingInsurance ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <CloudDownload sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}
              sx={{ ...brandButtonSx, py: .85, px: 2, fontSize: 13, whiteSpace: 'nowrap' }}
            >
              New Service
            </Button>
          </>
        }
      />

      <Box>
        <Box>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: ADMIN.radiusSm }} onClose={() => setError(null)}>{error}</Alert>}

          {/* Two panels, real CSS Grid. `align-items: start` is what closes the
              gap you spotted at the bottom: the panels were fixed at height 580
              regardless of content, so two records left roughly 400px of empty
              white below them. They size to their content now, and the service
              list caps its own scroll instead. */}
          <Box sx={{
            display: 'grid',
            gap: { xs: 2, md: 2.5 },
            alignItems: 'start',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '340px minmax(0, 1fr)' },
          }}>

            {/* Left: service list.
                From md up this becomes a STICKY, full-height card. Sized to its
                content it stopped short of the records panel beside it and left
                a band of canvas below — and since the panel exists to browse
                121 services, the honest thing is for it to use that height and
                show more of them. It also stays put while the records table
                scrolls, so you can keep clicking through services.

                Below md it is a normal block with its own capped scroller;
                a 100vh card on a phone would fill the screen and hide the
                records it is meant to load. */}
            <Box sx={{
              minWidth: 0,
              position: { md: 'sticky' },
              top: { md: ADMIN.stickyTop.sm },
              height: { md: `calc(100vh - ${ADMIN.stickyTop.sm}px - 16px)` },
            }}>
             <Paper elevation={0} sx={{ p: 2, border: `1px solid #DDE5EF`, borderRadius: ADMIN.radius, background: '#FFFFFF', display: 'flex', flexDirection: 'column', height: { md: '100%' }, overflow: 'hidden' }}>

  {/* Insurance filter */}
  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
    <Select
      displayEmpty
      value={insuranceFilter}
      onChange={e => setInsuranceFilter(e.target.value)}
      renderValue={selected => {
        if (!selected) return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#93A2B8' }}>
            <InsuranceIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>Filter by Insurance…</Typography>
          </Box>
        );
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InsuranceIcon sx={{ fontSize: 16, color: '#123A6D' }} />
            <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#123A6D', fontWeight: 600 }}>{selected}</Typography>
          </Box>
        );
      }}
      sx={{
        fontSize: '0.82rem',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: insuranceFilter ? '#123A6D' : '#DDE5EF' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#123A6D' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#123A6D' },
      }}
    >
      <MenuItem value=""><em style={{ color: '#93A2B8', fontSize: '0.82rem' }}>All Insurances</em></MenuItem>
      {insuranceProviders.map(ins => (
        <MenuItem key={ins.id} value={ins.name}>
          <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{ins.name}</Typography>
        </MenuItem>
      ))}
    </Select>
  </FormControl>

  {insuranceFilter && (
    <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Chip size="small" icon={<InsuranceIcon sx={{ fontSize: '13px !important' }} />}
        label={insuranceFilter} onDelete={() => setInsuranceFilter('')}
        sx={{ fontSize: '0.72rem', background: '#E8EEF7', color: '#123A6D', fontWeight: 600, height: 22 }} />
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>— filtering services</Typography>
    </Box>
  )}

  <TextField fullWidth size="small" placeholder="Search services..." value={searchTerm}
    onChange={e => setSearchTerm(e.target.value)} sx={{ mb: 1.5 }}
    InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#123A6D' }} /></InputAdornment>) }} />

  {/* ── Move toolbar ── */}
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F1F3D', fontSize: '0.8rem' }}>
        Available Services
      </Typography>
      {loadingServices && <CircularProgress size={14} />}
      <Chip size="small" label={`${services.length} total`}
        sx={{ fontSize: '0.68rem', background: '#E8EEF7', color: '#123A6D' }} />
    </Box>
    <Tooltip title={selectMode ? 'Exit selection mode' : 'Select services to move'}>
      <Button size="small" variant={selectMode ? 'contained' : 'outlined'}
        startIcon={<MoveIcon sx={{ fontSize: 14 }} />}
        onClick={() => { setSelectMode(s => !s); setSelectedServiceNames(new Set()); }}
        sx={{
          fontSize: '0.68rem', py: 0.3, px: 1,
          borderColor: '#123A6D', color: selectMode ? 'white' : '#123A6D',
          bgcolor: selectMode ? '#123A6D' : 'transparent',
          '&:hover': { bgcolor: selectMode ? '#0B2452' : 'rgba(18,58,109,0.05)' }
        }}>
        {selectMode ? 'Cancel' : 'Move'}
      </Button>
    </Tooltip>
  </Box>

  {/* ── Select all / Move action bar ── */}
  {selectMode && (
    <Box sx={{ mb: 1, p: 1, borderRadius: 1.5, bgcolor: 'rgba(18,58,109,0.05)', border: '1px solid rgba(18,58,109,0.18)', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#123A6D', fontSize: '0.72rem' }}>
        {selectedServiceNames.size} selected
      </Typography>
      <Button size="small" variant="text"
        onClick={() => handleSelectAllVisible(
          services
            .filter(s => s.service?.toLowerCase().includes(searchTerm.toLowerCase()))
            .filter(s => !insuranceFilter || s.insurances?.some(i => i.name === insuranceFilter))
        )}
        sx={{ fontSize: '0.68rem', py: 0.2, color: '#123A6D', minWidth: 0 }}>
        All
      </Button>
      <Button size="small" variant="text" onClick={handleDeselectAll}
        sx={{ fontSize: '0.68rem', py: 0.2, color: '#93A2B8', minWidth: 0 }}>
        None
      </Button>
      {selectedServiceNames.size > 0 && (
        <Button size="small" variant="contained"
          startIcon={<MoveIcon sx={{ fontSize: 13 }} />}
          onClick={() => setMoveDialogOpen(true)}
          sx={{ ml: 'auto', fontSize: '0.68rem', py: 0.3, px: 1.2, bgcolor: '#123A6D', '&:hover': { bgcolor: '#0B2452' } }}>
          Move {selectedServiceNames.size}
        </Button>
      )}
    </Box>
  )}

  {selectedService && !selectMode && (
    <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, background: 'linear-gradient(90deg,#E8EEF7,rgba(56,239,125,0.08))', border: '1px solid rgba(18,58,109,0.18)', display: 'flex', alignItems: 'center', gap: 1 }}>
      <CheckCircleIcon sx={{ fontSize: 16, color: '#2E9E4F' }} />
      <Typography variant="caption" sx={{ color: '#123A6D', fontWeight: 600 }}>Selected: {selectedService.service}</Typography>
      <IconButton size="small" onClick={() => { setSelectedService(null); setServiceInsuranceRows([]); }} sx={{ ml: 'auto', p: 0.2 }}>
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  )}

  {/* Fills whatever height the card has, and scrolls inside it.
      `minHeight: 0` is load-bearing: a flex child defaults to
      min-height:auto, so without it the list would refuse to shrink below its
      content height and would push the card taller than the viewport instead
      of scrolling. On a phone there is no fixed card height, so it falls back
      to a 420px cap. */}
  <Box sx={{
    flex: { md: 1 },
    minHeight: 0,
    maxHeight: { xs: 420, md: 'none' },
    overflowY: 'auto',
    pr: 0.5,
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' },
  }}>
    {loadingServices ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
    ) : services.length === 0 ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 1 }}>
        <ServiceIcon sx={{ fontSize: 40, color: '#bdbdbd' }} />
        <Typography variant="body2" color="text.secondary">No services found.</Typography>
      </Box>
    ) : (
      services
        .filter(s => s.service?.toLowerCase().includes(searchTerm.toLowerCase()))
        .filter(s => !insuranceFilter || s.insurances?.some(i => i.name === insuranceFilter))
        .map(service => {
          const isChecked = selectedServiceNames.has(service.service);
          const isActive  = selectedService?.id === service.id && !selectMode;
          return (
            <Box key={service.id}
              onClick={() => selectMode ? toggleServiceSelection(service.service) : handleServiceClick(service)}
              sx={{
                display: 'flex', alignItems: 'center', mb: 0.75, p: 1.25, borderRadius: 1.5,
                cursor: 'pointer', transition: 'all 0.18s ease',
                border: isActive ? '1.5px solid #123A6D' : isChecked ? '1.5px solid #2E9E4F' : '1.5px solid transparent',
                background: isActive ? 'rgba(102,126,234,0.08)' : isChecked ? 'rgba(17,153,142,0.07)' : 'transparent',
                '&:hover': { background: 'rgba(102,126,234,0.05)', transform: 'translateX(3px)', borderColor: 'rgba(102,126,234,0.3)' }
              }}>
              {selectMode ? (
                <Checkbox size="small" checked={isChecked}
                  onChange={() => toggleServiceSelection(service.service)}
                  onClick={e => e.stopPropagation()}
                  sx={{ p: 0.3, color: '#123A6D', '&.Mui-checked': { color: '#2E9E4F' }, mr: 0.5 }} />
              ) : (
                <ServiceIcon sx={{ fontSize: 16, mr: 1, color: isActive ? '#123A6D' : '#bdbdbd', transition: 'color 0.18s' }} />
              )}
              <Typography variant="body2" sx={{ fontSize: '0.85rem', color: isActive ? '#123A6D' : '#424242', fontWeight: isActive || isChecked ? 700 : 400 }}>
                {service.service}
              </Typography>
              {isChecked && selectMode && (
                <CheckCircleIcon sx={{ fontSize: 14, color: '#2E9E4F', ml: 'auto' }} />
              )}
            </Box>
          );
        })
    )}
  </Box>
</Paper>
            </Box>

            {/* Right: editable records table */}
            <Box sx={{ minWidth: 0 }}>
              {/* No fixed height. This was `height: 580` too, which is where
                  the empty band under the two records came from. */}
              <Paper elevation={0} sx={{ border: `1px solid #DDE5EF`, borderRadius: ADMIN.radius, background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #DDE5EF', background: '#F4F7FB', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F1F3D' }}>Insured Services Records</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedService ? `Records for: ${selectedService.service}` : 'Select a service to load records'}
                    </Typography>
                  </Box>
                  {selectedService && !loadingServiceInsurances && (
                    <Chip icon={<InsuranceIcon sx={{ fontSize: '14px !important' }} />}
                      label={`${serviceInsuranceRows.length} record${serviceInsuranceRows.length !== 1 ? 's' : ''}`} size="small"
                      sx={{ background: serviceInsuranceRows.length > 0 ? '#E7F5EC' : '#fce4ec', color: serviceInsuranceRows.length > 0 ? '#217A3C' : '#B23A3A', fontWeight: 600, fontSize: '0.7rem' }} />
                  )}
                </Box>

                {/* Bulk edit bar — type once, apply to every ticked scheme. */}
                {selectedService && serviceInsuranceRows.length > 0 && !loadingServiceInsurances && (
                  <BulkEditBar
                    fields={BULK_FIELDS}
                    values={bulkValues}
                    onChange={(k, v) => setBulkValues(prev => ({ ...prev, [k]: v }))}
                    selectedCount={selectedRowIds.length}
                    totalCount={serviceInsuranceRows.length}
                    onApply={handleBarApply}
                    onDelete={handleBarDelete}
                    applying={bulkApplying}
                    deleting={bulkDeleting}
                    itemLabel="scheme"
                  />
                )}

                {/* The three placeholder states used to fill a 580px panel with
                    a faded pastel icon. They are normal empty states now, so a
                    short panel stays short. */}
                {!selectedService ? (
                  <EmptyState
                    icon={InsuranceIcon}
                    title="Pick a service"
                    body="Choose one from the list on the left to see and edit the tariff each scheme is charged."
                  />
                ) : loadingServiceInsurances ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, gap: 1.5 }}>
                    <CircularProgress size={20} sx={{ color: '#123A6D' }} />
                    <Typography sx={{ fontSize: 13, color: '#5A6B87' }}>Loading records…</Typography>
                  </Box>
                ) : serviceInsuranceRows.length === 0 ? (
                  <EmptyState
                    icon={InsuranceIcon}
                    title="No schemes on this service"
                    body={`Nothing is configured for ${selectedService.service} yet.`}
                  />
                ) : (
                  <TableContainer sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { width: '6px', height: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' } }}>
                    <Table size="small" sx={{ minWidth: 860 }}>
                      <TableHead>
                        <TableRow>
                          {/* Was a solid green band with white text — the third
                              green on the screen, and heavier than the data
                              under it. Quiet uppercase labels on the canvas
                              tint, like every other admin table. */}
                          {/* Select-all sits in the header, as in Diagnosis and
                              Procedure — so the three screens work the same way. */}
                          <TableCell sx={{ background: '#F4F7FB', borderBottom: '1px solid #DDE5EF', width: 44, textAlign: 'center', py: 1.25, px: 0.5 }}>
                            <Checkbox
                              size="small"
                              checked={selectedRowIds.length === serviceInsuranceRows.length && serviceInsuranceRows.length > 0}
                              indeterminate={selectedRowIds.length > 0 && selectedRowIds.length < serviceInsuranceRows.length}
                              onChange={handleSelectAllRows}
                              sx={{ p: 0.5, color: '#93A2B8', '&.Mui-checked': { color: '#2E9E4F' }, '&.MuiCheckbox-indeterminate': { color: '#2E9E4F' } }}
                            />
                          </TableCell>
                          {/* 'Code' renamed to 'GDRG Code' — that column holds
                              the GDRG the claim is billed on. */}
                          {['#', 'Insurance', 'Charge Status', 'Tariff (GH¢)', 'Top-up', 'Age', 'GDRG Code', 'Actions'].map(label => (
                            <TableCell key={label} sx={{ background: '#F4F7FB', color: '#93A2B8', fontWeight: 800, fontSize: '0.65rem', letterSpacing: '1px', textTransform: 'uppercase', borderBottom: '1px solid #DDE5EF', py: 1.25, whiteSpace: 'nowrap', ...(label === '#' && { width: 36, textAlign: 'center' }), ...(label === 'Actions' && { width: 170, textAlign: 'center' }) }}>
                              {label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {serviceInsuranceRows.map((row, idx) => (
                          <EditableRow key={row.id || idx} row={row} idx={idx}
                            selected={selectedRowIds.includes(row.id)}
                            onSelectToggle={handleRowSelectionToggle}
                            onChange={(field, value) => handleRowChange(idx, field, value)}
                            onUpdate={handleUpdateRow} onDelete={handleDeleteRow}
                            updating={updatingRowId === row.id} deleting={deletingRowId === row.id} />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ══════════ Move Services Dialog ══════════ */}
<Dialog open={moveDialogOpen} onClose={() => !moving && setMoveDialogOpen(false)}
  maxWidth="xs" fullWidth TransitionComponent={Fade}
  PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>

  <DialogTitle sx={{ p: 0 }}>
    <Box sx={{ background: 'linear-gradient(135deg,#123A6D,#764ba2)', color: 'white', px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <MoveIcon sx={{ fontSize: 26 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>Move Services</Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {selectedServiceNames.size} service{selectedServiceNames.size !== 1 ? 's' : ''} selected
          </Typography>
        </Box>
      </Box>
      <IconButton onClick={() => setMoveDialogOpen(false)} disabled={moving} sx={{ color: 'white' }}>
        <CloseIcon />
      </IconButton>
    </Box>
  </DialogTitle>

  <DialogContent sx={{ pt: 3, pb: 1 }}>
    {/* Selected services summary */}
    <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: '#f8f9ff', border: '1px solid #E8EEF7', maxHeight: 140, overflowY: 'auto' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#123A6D', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem', display: 'block', mb: 1 }}>
        Services to copy:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {Array.from(selectedServiceNames).map(name => (
          <Chip key={name} label={name} size="small"
            sx={{ fontSize: '0.72rem', height: 20, bgcolor: '#E8EEF7', color: '#123A6D' }} />
        ))}
      </Box>
    </Box>

    {/* Target insurance picker */}
    <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', display: 'block', mb: 0.75 }}>
      Move to Insurance *
    </Typography>
    <FormControl fullWidth size="small">
      <Select displayEmpty value={moveTargetInsurance}
        onChange={e => setMoveTargetInsurance(e.target.value)}
        renderValue={v => v
          ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InsuranceIcon sx={{ fontSize: 16, color: '#123A6D' }} />
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#123A6D' }}>{v}</Typography>
            </Box>
          : <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#93A2B8' }}>Select target insurance…</Typography>
        }
        sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: moveTargetInsurance ? '#123A6D' : '#DDE5EF' } }}>
        {insuranceProviders.map(ins => (
          <MenuItem key={ins.id} value={ins.name}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InsuranceIcon sx={{ fontSize: 15, color: '#123A6D' }} />
              <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{ins.name}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem', py: 0.5 }}>
      Services already assigned to the target insurance will be skipped.
    </Alert>
  </DialogContent>

  <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid #DDE5EF' }}>
    <Button onClick={() => setMoveDialogOpen(false)} disabled={moving} variant="outlined"
      sx={{ color: '#757575', borderColor: '#DDE5EF', flex: 1 }}>
      Cancel
    </Button>
    <Button variant="contained" onClick={handleMoveServices}
      disabled={moving || !moveTargetInsurance}
      startIcon={moving ? <CircularProgress size={16} color="inherit" /> : <MoveIcon />}
      sx={{ flex: 1, background: 'linear-gradient(90deg,#123A6D,#764ba2)', fontWeight: 700, '&:hover': { filter: 'brightness(0.9)' }, '&:disabled': { background: '#DDE5EF', color: '#93A2B8' } }}>
      {moving ? 'Moving…' : `Move ${selectedServiceNames.size}`}
    </Button>
  </DialogActions>
</Dialog>


      <NewServiceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        insuranceProviders={insuranceProviders}
        loadingInsurance={loadingInsurance}
        onSaved={loadServicesFromBackend}
      />
    </>
  );
};

export default ServicesSetting;